import { supabase } from '@/lib/integrations/supabase'
import { validateCapability } from '@/lib/permissions/permissions'
import type { Role } from '@/lib/permissions/types'
import type {
  FinancialEntry,
  RecurrenceRules,
  RecurrenceTemplate,
  RecurrenceTemplateInput,
  UpdateEntryInput,
  VirtualEntry,
} from './entriesTypes'
// â”€â”€â”€ Recurrence Templates â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Create a new recurrence template and return it. */
export async function createRecurrenceTemplate(
  input: RecurrenceTemplateInput,
): Promise<RecurrenceTemplate> {
  // Determine origin_amount: prefer explicit input; otherwise attempt to
  // reconstruct the original full amount by summing the origin entry plus
  // any immediate child parcels (created by partial payments). This lets
  // us recover the original value even when a partial payment already
  // modified the origin row.
  let originAmount = input.origin_amount ?? 0
  try {
    if (!originAmount || originAmount === 0) {
      const { data: related, error: relErr } = await supabase
        .from('financial_entries')
        .select('amount')
        .or(`id.eq.${input.origin_entry_id},parent_id.eq.${input.origin_entry_id}`)
      if (relErr) {
        console.warn('[createRecurrenceTemplate] failed to fetch related entries for origin_amount', relErr)
      } else if (related && related.length > 0) {
        originAmount = related.reduce((s: number, r: any) => s + Number(r.amount || 0), 0)
      }
    }
  } catch (err) {
    console.warn('[createRecurrenceTemplate] error while computing origin_amount', err)
  }

  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .insert({
      unit_id:         input.unit_id,
      origin_entry_id: input.origin_entry_id,
      periodicity:     input.periodicity,
      rules_json:      input.rules_json ?? {},
      start_date:      input.start_date,
      origin_amount:   originAmount ?? 0,
    })
    .select()
    .single()
  if (error) throw error
  return data as RecurrenceTemplate
}

/** Fetch the recurrence template linked to a specific entry (by origin_entry_id). */
export async function getRecurrenceTemplateByEntry(
  originEntryId: string,
): Promise<RecurrenceTemplate | null> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('origin_entry_id', originEntryId)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

/** List all active recurrence templates for a unit. */
export async function listRecurrenceTemplates(
  unitId: string,
): Promise<RecurrenceTemplate[]> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .eq('status', 'active')
    .order('start_date', { ascending: true })
  if (error) throw error
  return (data as RecurrenceTemplate[]) || []
}

/** Update the rules or periodicity of an existing template. */
export async function updateRecurrenceTemplate(
  id: string,
  patch: Partial<Pick<RecurrenceTemplate, 'periodicity' | 'rules_json' | 'end_date'>>,
): Promise<RecurrenceTemplate> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as RecurrenceTemplate
}

/** Terminate a recurrence template â€” sets status='terminated' and end_date=today. */
export async function terminateRecurrenceTemplate(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canTerminateRecurrences')
  }
  const today = new Date().toISOString().split('T')[0]
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .update({ status: 'terminated', end_date: today })
    .eq('id', id)
  if (error) throw error
}

/**
 * Terminates a recurrence template by hard-deleting it.
 *
 * Terminates a recurrence template.
 *
 * - If `endDate` is in the FUTURE: soft-terminate (status='terminated', end_date set).
 *   The template must remain alive so that virtual entries between now and end_date
 *   can still be generated and materialised.  Expired soft-terminated rows are
 *   automatically removed by cleanupExpiredRecurrences().
 *
 * - Otherwise (no endDate, or endDate is today / in the past): hard-delete.
 *   The FK ON DELETE SET NULL clears recurrence_template_id on every linked entry.
 */
export async function smartTerminateRecurrenceTemplate(
  templateId:    string,
  originEntryId: string,
  endDate?:      string,
  userRole?:     Role,
): Promise<{ wasDeleted: boolean }> {
  if (userRole) {
    await validateCapability(userRole, 'canTerminateRecurrences')
  }
  const today = new Date().toISOString().split('T')[0]
  const hasFutureEndDate = endDate != null && endDate > today

  if (hasFutureEndDate) {
    // Soft-terminate: keep template so remaining virtual entries are generated until end_date
    const { error } = await supabase
      .from('financial_entry_recurrences')
      .update({ status: 'terminated', end_date: endDate })
      .eq('id', templateId)
    if (error) throw error
    return { wasDeleted: false }
  } else {
    // Hard-delete: FK ON DELETE SET NULL unlinks all materialized entries automatically
    const { error } = await supabase
      .from('financial_entry_recurrences')
      .delete()
      .eq('id', templateId)
    if (error) throw error
    return { wasDeleted: true }
  }
}

/**
 * Count how many financial entries are linked to a recurrence template
 * (other than the origin entry itself).
 */
export async function countEntriesWithTemplate(templateId: string, originEntryId: string): Promise<number> {
  const { count, error } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .eq('recurrence_template_id', templateId)
    .neq('id', originEntryId)
  if (error) throw error
  return count ?? 0
}

/**
 * For installment recurrences: deletes the template when every installment slot
 * has been materialized (i.e. a non-canceled entry exists for each expected slot).
 *
 * The FK ON DELETE SET NULL on financial_entries.recurrence_template_id
 * automatically unlinks every materialized entry when the template is deleted.
 *
 * Safe to call for non-installment templates â€” returns immediately.
 */
export async function maybeCleanupInstallmentTemplate(
  template: RecurrenceTemplate,
): Promise<void> {
  if (template.periodicity !== 'installment') return

  const rules   = template.rules_json
  const total   = rules?.installment_total         ?? 0
  const start   = rules?.installment_current_start ?? 1
  const expected = total - start + 1
  if (expected <= 0) return

  // Count non-canceled entries covering this template, including the origin entry
  // (which may or may not have recurrence_template_id set at the time of the check).
  const { count, error: countErr } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .or(`recurrence_template_id.eq.${template.id},id.eq.${template.origin_entry_id}`)
    .neq('status', 'canceled')
  if (countErr) throw countErr

  if ((count ?? 0) >= expected) {
    const { error: delErr } = await supabase
      .from('financial_entry_recurrences')
      .delete()
      .eq('id', template.id)
    if (delErr) throw delErr
    // FK ON DELETE SET NULL handles unlinking all entries automatically
  }
}

/**
 * Rolls the template's origin_entry_id to the most recently materialised entry,
 * keeping the template always anchored to current data.
 *
 * For non-installment recurrences also clears recurrence_template_id from entries
 * whose occurrence date is older than 35 days â€” safely outside the dedup window
 * used by get_virtual_entries â€” so that old paid entries are not permanently bound
 * to the template.
 *
 * Installment series are intentionally left intact: all parcel links must remain
 * for maybeCleanupInstallmentTemplate to count them correctly.
 *
 * Safe to call even if the template was already deleted (update is a no-op).
 */
export async function rollTemplateOrigin(
  template:         RecurrenceTemplate,
  newOriginEntryId: string,
): Promise<void> {
  // Update origin â€” template may have been deleted by maybeCleanupInstallmentTemplate;
  // in that case this update affects 0 rows without error.
  const { error: updateErr } = await supabase
    .from('financial_entry_recurrences')
    .update({ origin_entry_id: newOriginEntryId })
    .eq('id', template.id)
  if (updateErr) throw updateErr

  // For installment series: keep all links so completion counting stays accurate
  if (template.periodicity === 'installment') return

  // Clear entries outside the 35-day dedup window
  // Uses COALESCE(original_due_date, due_date) logic via .or()
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 35)
  const cutoffDate = cutoff.toISOString().split('T')[0]

  const { error: clearErr } = await supabase
    .from('financial_entries')
    .update({ recurrence_template_id: null })
    .eq('recurrence_template_id', template.id)
    .neq('id', newOriginEntryId)
    .or(
      `and(original_due_date.not.is.null,original_due_date.lt.${cutoffDate}),` +
      `and(original_due_date.is.null,due_date.lt.${cutoffDate})`,
    )
  if (clearErr) throw clearErr
}

/**
 * Before deleting an entry that is the template's current origin_entry_id,
 * hands the origin role to the most recent OTHER linked entry so that
 * the template is not cascade-deleted unexpectedly.
 *
 * Returns true if the baton was passed successfully.
 * Returns false if no candidate exists â€” caller should let the cascade proceed.
 */
export async function handoverTemplateOrigin(
  templateId:       string,
  currentOriginId:  string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('id')
    .eq('recurrence_template_id', templateId)
    .neq('id', currentOriginId)
    .order('due_date', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  if (!data) return false

  const { error: updateErr } = await supabase
    .from('financial_entry_recurrences')
    .update({ origin_entry_id: data.id })
    .eq('id', templateId)
  if (updateErr) throw updateErr
  return true
}

/**
 * Deletes soft-terminated recurrence templates whose end_date has already passed.
 * These templates can no longer generate virtual entries (get_virtual_entries
 * filters WHERE t.status = 'active') and serve no further purpose.
 *
 * Called lazily from fetchVirtualEntries so the cleanup is transparent to the user.
 * Wrapped in try-catch at call-site â€” failure is non-critical.
 */
export async function cleanupExpiredRecurrences(unitId: string): Promise<void> {
  const today = new Date().toISOString().split('T')[0]
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .delete()
    .eq('unit_id', unitId)
    .eq('status', 'terminated')
    .lt('end_date', today)
  if (error) throw error
}

/**
 * Find the immediately preceding terminated template for a given template.
 * Looks for a terminated template whose end_date equals the day before 
 * the current template's start_date.
 *
 * This is used when a user deletes an entry from a template created by
 * "esta e futuras" â€” to check if we should re-activate the previous template.
 */
export async function findPreviousTerminatedTemplate(
  unitId: string,
  currentTemplateStartDate: string,
): Promise<RecurrenceTemplate | null> {
  // Calculate the day before start_date
  const startDate = new Date(currentTemplateStartDate + 'T12:00:00')
  startDate.setDate(startDate.getDate() - 1)
  const dayBefore = startDate.toISOString().split('T')[0]

  // The previous template may have status 'active' (just capped with end_date) or 'terminated'.
  // When "esta e futuras" is applied, the old template keeps status='active' but gets end_date set.
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .eq('end_date', dayBefore)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

/**
 * Reactivate a terminated template by removing its end_date.
 * Used when a user cancels a "esta e futuras" operation by deleting the
 * entry created during that split.
 */
export async function reactivateTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('financial_entry_recurrences')
    .update({ status: 'active', end_date: null })
    .eq('id', templateId)
  if (error) throw error
}

/** Get a recurrence template by its ID. */
export async function getRecurrenceTemplate(id: string): Promise<RecurrenceTemplate | null> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return (data as RecurrenceTemplate) || null
}

// â”€â”€â”€ Virtual entry computation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// â”€â”€ Internal date helpers (mirror the UI helpers so logic lives in one place) â”€â”€

function _addMonths(dateStr: string, n: number, targetDay?: number): string {
  const [y, m, d]   = dateStr.split('-').map(Number)
  const targetMonth = (m - 1) + n
  const targetYear  = y + Math.floor(targetMonth / 12)
  const finalMonth  = ((targetMonth % 12) + 12) % 12
  const maxDay      = new Date(targetYear, finalMonth + 1, 0).getDate()
  // Use targetDay if provided, otherwise use original day
  const dayToUse    = targetDay ?? d
  return `${targetYear}-${String(finalMonth + 1).padStart(2, '0')}-${String(Math.min(dayToUse, maxDay)).padStart(2, '0')}`
}

function _addYears(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const maxDay    = new Date(y + n, m, 0).getDate()
  return `${y + n}-${String(m).padStart(2, '0')}-${String(Math.min(d, maxDay)).padStart(2, '0')}`
}

function _addWeeks(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  date.setDate(date.getDate() + n * 7)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  date.setDate(date.getDate() + n)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _toDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function _snapToWeekday(dateStr: string, dow: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  const diff      = (dow - date.getDay() + 7) % 7
  if (diff > 0) date.setDate(date.getDate() + diff)
  return _toDateStr(date)
}

function _adjustWeekend(dateStr: string, mode: 'next' | 'prev'): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date      = new Date(y, m - 1, d)
  const dow       = date.getDay()
  if (dow === 0) date.setDate(date.getDate() + (mode === 'next' ? 1  : -2))
  if (dow === 6) date.setDate(date.getDate() + (mode === 'next' ? 2  : -1))
  return _toDateStr(date)
}

function _applyRules(dateStr: string, rules: RecurrenceRules): string {
  let result = dateStr

  // Check if day-of-week is allowed (for daily periodicity with weekday filter)
  if (rules.daily_weekdays && rules.daily_weekdays.length > 0) {
    const dow = new Date(dateStr + 'T00:00:00').getDay()
    if (!rules.daily_weekdays.includes(dow)) {
      return '' // Signal to skip this date
    }
  }

  if (rules.weekday_lock && rules.weekday_lock_day != null)
    result = _snapToWeekday(result, rules.weekday_lock_day)
  if (rules.weekend_adjust && rules.weekend_adjust !== 'none')
    result = _adjustWeekend(result, rules.weekend_adjust)
  return result
}

// â”€â”€ Public service functions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/**
 * Fetch recurrence templates for a unit that are relevant for virtual entry generation.
 * Includes both 'active' templates and 'terminated' ones (which still need to generate
 * virtual entries up to their end_date).
 */
export async function listActiveRecurrenceTemplates(
  unitId: string,
): Promise<RecurrenceTemplate[]> {
  const { data, error } = await supabase
    .from('financial_entry_recurrences')
    .select('*')
    .eq('unit_id', unitId)
    .in('status', ['active', 'terminated'])
  if (error) throw error
  return (data as RecurrenceTemplate[]) || []
}

/**
 * Calculate all occurrence dates for a template that fall within [monthStart, monthEnd].
 *
 * - `start_date` is the anchor (first real entry date).
 * - `end_date`   limits the series; NULL = open-ended.
 * - Returns raw date strings 'YYYY-MM-DD' with rules_json applied.
 */
export function generateRecurrenceDates(
  template:   RecurrenceTemplate,
  monthStart: string,   // 'YYYY-MM-DD'  (first day of visible month)
  monthEnd:   string,   // 'YYYY-MM-DD'  (last  day of visible month)
): string[] {
  const { periodicity, rules_json: rules, start_date, end_date } = template
  const seriesEnd = end_date ?? monthEnd   // cap at month when open-ended

  const dates: string[] = []

  if (periodicity === 'daily') {
    // Walk day-by-day starting from the day AFTER start_date until we pass monthEnd or seriesEnd
    // Safety cap: 36500 iterations (~100 years of daily)
    for (let i = 1; i <= 36500; i++) {
      const cursor   = _addDays(start_date, i)
      const adjusted = _applyRules(cursor, rules)
      if (adjusted === '') continue  // Skip if day-of-week filter excluded this date
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
    }

  } else if (periodicity === 'monthly') {
    // Walk month-by-month from start_date until we pass monthEnd or seriesEnd
    let cursor = start_date
    const targetDay = rules.monthly_target_day
    // Safety cap: 1200 iterations (~100 years of monthly)
    for (let i = 0; i < 1200; i++) {
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addMonths(start_date, i + 1, targetDay)
      if (cursor > monthEnd && cursor > seriesEnd) break
    }

  } else if (periodicity === 'weekly') {
    const dow = rules.weekly_dow ?? new Date(
      Number(start_date.slice(0, 4)),
      Number(start_date.slice(5, 7)) - 1,
      Number(start_date.slice(8, 10)),
    ).getDay()
    // Find first occurrence on or after start_date on the right day-of-week
    let cursor = _snapToWeekday(start_date, dow)
    for (let i = 0; i < 5200; i++) {   // 100 years of weekly
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addWeeks(cursor, 1)
    }

  } else if (periodicity === 'yearly') {
    let cursor = start_date
    for (let i = 0; i < 200; i++) {    // 200 years of yearly
      const adjusted = _applyRules(cursor, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
      cursor = _addYears(start_date, i + 1)
    }

  } else if (periodicity === 'installment') {
    const total = rules.installment_total ?? 1
    const targetDay = rules.monthly_target_day
    for (let i = 0; i < total; i++) {
      const raw      = _addMonths(start_date, i, targetDay)
      const adjusted = _applyRules(raw, rules)
      if (adjusted > monthEnd || adjusted > seriesEnd) break
      if (adjusted >= monthStart) dates.push(adjusted)
    }
  }

  return dates
}

/**
 * Fetch virtual entries for a unit within a date range.
 * Calls the get_virtual_entries RPC (migration 061) which generates occurrences
 * on demand â€” no artificial cutoff, calculates until template's end_date or
 * the user explicitly terminates the recurrence.
 *
 * @param unitId   The unit to fetch virtual entries for
 * @param fromDate Start of range (YYYY-MM-DD)
 * @param toDate   End of range (YYYY-MM-DD)
 */
export async function fetchVirtualEntries(
  unitId: string,
  fromDate: string,
  toDate: string,
): Promise<VirtualEntry[]> {
  // Lazy cleanup: remove soft-terminated templates whose end_date has passed
  try { await cleanupExpiredRecurrences(unitId) } catch { /* non-critical */ }

  const { data, error } = await supabase.rpc('get_virtual_entries', {
    p_unit_id:   unitId,
    p_from_date: fromDate,
    p_to_date:   toDate,
  })

  if (error) throw error

  return (data || []).map((row: Record<string, unknown>) => ({
    ...row,
    isVirtual: true,
    _template: {
      id: row.recurrence_template_id,
      unit_id: row.unit_id,
      origin_entry_id: row.id,
      periodicity: 'monthly' as const,
      rules_json: {},
      start_date: row.due_date,
      end_date: null,
      origin_amount: row.amount,
      status: 'active' as const,
      created_by: row.created_by,
      created_at: row.created_at,
      updated_at: row.updated_at,
    },
  } as VirtualEntry))
}

/**
 * Count future open entries that share the same recurrence_id as `entry`
 * and have a due_date strictly after `entry.due_date`.
 */
export async function countFutureRecurrences(entry: FinancialEntry): Promise<number> {
  if (!entry.recurrence_id) return 0
  const { count, error } = await supabase
    .from('financial_entries')
    .select('id', { count: 'exact', head: true })
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
  return count ?? 0
}

/**
 * Fetch the next entry in the same recurrence chain (due_date strictly after `afterDate`).
 */
export async function fetchNextRecurrence(recurrenceId: string, afterDate: string): Promise<FinancialEntry | null> {
  if (!recurrenceId) return null
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('recurrence_id', recurrenceId)
    .gt('due_date', afterDate)
    .order('due_date', { ascending: true })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as FinancialEntry) || null
}

/**
 * Apply `input` (everything except due_date) to all future open entries
 * in the same recurrence chain.
 */
export async function updateFutureRecurrences(
  entry: FinancialEntry,
  input: Omit<UpdateEntryInput, 'due_date'>,
): Promise<void> {
  if (!entry.recurrence_id) return
  if (Object.keys(input).length === 0) return
  const { error } = await supabase
    .from('financial_entries')
    .update(input)
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
}

/**
 * Delete all future open entries in the same recurrence chain.
 */
export async function deleteFutureRecurrences(entry: FinancialEntry): Promise<void> {
  if (!entry.recurrence_id) return
  const { error } = await supabase
    .from('financial_entries')
    .delete()
    .eq('recurrence_id', entry.recurrence_id)
    .neq('id', entry.id)
    .eq('status', 'open')
    .gt('due_date', entry.due_date)
  if (error) throw error
}

